/**
 * NPM Reports Hub + Audit & Timesheet Reports (v2).
 *
 * Replaces four v1 sidebar entries with one page: three week-scoped .xlsx
 * downloads as cards, plus a per-employee PDF builder reached from a fourth
 * card. Built from docs/featureBriefs/design_handoff_npm_reports_hub/ — see the
 * C1–C11 review corrections at the top of that bundle's CLAUDE_CODE_HANDOFF.md,
 * which supersede parts of the design.
 *
 * Week comes from the v2 shell's global pill; this page renders no picker of
 * its own. The sub-view is view state inside this page, not a second route.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Activity, AlertOctagon, AlertTriangle, Calendar, CalendarOff, Check, CheckCircle2,
  ChevronRight, Clock, Download, FileBarChart2, FileSearch, FileSpreadsheet, FileText, Info,
  ListChecks, MessageSquareText, RefreshCw, Search, TrendingUp, User, UserCheck, UserSearch, Users, X,
} from 'lucide-react';
import { useSiteContext } from '../../SiteContext';
import config from '../../configLoader';
import { can } from '../../util/adminPermissions';
import FloatMenuButton, { FloatMenuOption } from '../../components/v2/FloatMenuButton';
import EmployeeViewManager from '../../managers/EmployeeViewManager';
import { EmployeeInfo } from '../../interfaces/EmployeeInfoInterface';
import { Timesheet } from '../../interfaces/AuditInterface';
import {
  getEmployeeTimesheets, generateAuditReport, generateTimePunchReport, generateTimesheetReport,
} from '../../api/audit';
import { downloadAttendanceReport } from '../../api/attendanceReport';
import { downloadTimesheetDeadlineReport } from '../../api/timesheetDeadlineReport';
import { downloadWeeklyOpsReport } from '../../api/weeklyOpsReport';
import {
  downloadMiscTaskTrendReport, getMiscTaskTrendSupervisors,
  MiscTaskTrendFormat, MiscTaskTrendSupervisor,
} from '../../api/miscTaskTrending';
import {
  displayName, formatRange, groupByYear, hueOf, initialsOf, missingWeeksBetween,
} from './reportsHubHelpers';
import {
  MISC_TASK_TREND_INCLUSIONS, miscTaskTrendFileName, resolveDefaultSupervisor, supervisorLabel,
} from './miscTaskTrendHelpers';
import './ReportsHubPage.css';

/** Soft-warning threshold. One accounting year is ~52 weeks, a quarter 13; 24
 *  sits above every routine selection and below the genuinely expensive ones.
 *  Revisit if the N× query in auditReportGenerator.ts ever gets fixed. */
const WARN_AT = 24;

type ReportID = 'audit' | 'punch' | 'timesheet';
type CardID = 'attendance' | 'deadline' | 'ops';
type CardState = 'idle' | 'busy' | 'done';

interface ReportDef {
  id: ReportID;
  name: string;
  desc: string;
  isNew?: boolean;
  /** Mirrors the PDF's own legend so the chooser previews what prints.
   *  Hexes are verbatim from auditTimePunchReportGenerator.ts:41-52. */
  legend?: Array<[string, string]>;
}

const REPORTS: ReportDef[] = [
  { id: 'audit', name: 'Audit Report', desc: "Each week's timecard, followed by every recorded change to that week." },
  {
    id: 'punch', name: 'Audit Time Punch Report',
    desc: "Each week's timecard, then every punch day by day, colour-coded by how it was entered, with the changes that touched it.",
    legend: [['#D6E8FF', 'Button'], ['#EAFF2E', 'Edit'], ['#FE9900', 'Manually added']],
  },
  { id: 'timesheet', name: 'Timesheet Report', isNew: true, desc: 'Just the timecards for the weeks you pick. Nothing else.' },
];

interface CardDef {
  id: CardID;
  name: string;
  Icon: typeof UserCheck;
  tone: string;
  desc: string;
  download: (payPeriodID: number, fileName: string) => Promise<void>;
  fileName: (weekNumber: number, accountingYear: number) => string;
}

/** NOTE: these three descriptions are draft copy written from the report names,
 *  not from the spreadsheets. Bundle open item #1 — payroll to confirm. */
const CARDS: CardDef[] = [
  {
    id: 'attendance', name: 'Attendance Report', Icon: UserCheck, tone: 'var(--c-so)',
    desc: "Who worked which days this week and who didn't — one row per employee, with daily hours and absences.",
    download: downloadAttendanceReport,
    fileName: (w, y) => `AttendanceReport_Week${w}_${y}.xlsx`,
  },
  {
    id: 'deadline', name: 'Timesheet Deadline Report', Icon: Clock, tone: 'var(--warn)',
    desc: "Who has and hasn't submitted for the week, so crews can be chased before the payroll deadline.",
    download: downloadTimesheetDeadlineReport,
    fileName: (w) => `TimesheetDeadlineReport_Week${w}.xlsx`,
  },
  {
    id: 'ops', name: 'Weekly Ops Report', Icon: Activity, tone: 'var(--c-holiday)',
    desc: "The week's hours rolled up for the ops review, by employee and by location.",
    download: downloadWeeklyOpsReport,
    fileName: (w) => `WeeklyOpsReport_Week${w}.xlsx`,
  },
];

const ALL = 'ALL';

/* ── component ────────────────────────────────────────────────────────────── */

const ReportsHubPage: React.FC = () => {
  const { userRole, locations, payPeriodID, payPeriods, createPopup } = useSiteContext();

  /** Renamed from the original 'hub' | 'sub' when a third screen arrived — with
   *  more than one sub-view, "sub" no longer names anything. */
  const [view, setView] = useState<'hub' | 'audit' | 'miscTrend'>('hub');

  // hub
  const [cards, setCards] = useState<Record<CardID, CardState>>({ attendance: 'idle', deadline: 'idle', ops: 'idle' });
  const [cardDoneAt, setCardDoneAt] = useState<Record<string, string>>({});

  // sub-view — left
  const [employees, setEmployees] = useState<EmployeeInfo[]>([]);
  const [empLoading, setEmpLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [locationID, setLocationID] = useState<string>(ALL);
  const [supervisorID, setSupervisorID] = useState<string>(ALL);
  const [activeOnly, setActiveOnly] = useState(false); // C9 — audit packets are usually for people who left
  const [selectedEmployeeID, setSelectedEmployeeID] = useState<number | null>(null);

  // sub-view — right
  const [periods, setPeriods] = useState<Timesheet[]>([]);
  const [periodsLoading, setPeriodsLoading] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [openYears, setOpenYears] = useState<Set<string>>(new Set());
  const [report, setReport] = useState<ReportID>('audit');
  const [includeComments, setIncludeComments] = useState(true);
  const [gen, setGen] = useState<'idle' | 'busy' | 'failed'>('idle');

  // misc task trending sub-view
  const [trendSupervisors, setTrendSupervisors] = useState<MiscTaskTrendSupervisor[]>([]);
  const [trendLoading, setTrendLoading] = useState(false);
  const [trendSupervisorID, setTrendSupervisorID] = useState<number | null>(null);
  const [trendBusy, setTrendBusy] = useState<'idle' | MiscTaskTrendFormat>('idle');

  const [toasts, setToasts] = useState<Array<{ id: number; msg: string }>>([]);
  const toastSeq = useRef(0);

  const showToast = useCallback((msg: string) => {
    const id = ++toastSeq.current;
    setToasts((t) => [...t, { id, msg }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 2600);
  }, []);

  /** The comments toggle only ever takes comments away — the server still
   *  requires the role. Users without it never saw comments and still won't,
   *  so they get no control rather than a disabled one. */
  const canSeeComments = userRole.admin || userRole.hr || userRole.payroll;

  /**
   * Per-card gate for Misc Task Trending — handoff rule C8's per-card model,
   * which the first pass of this page never implemented (it reads no config at
   * all and shows every card to any tenant that turns the hub on).
   *
   * `viewMiscTasks` is here alongside `viewReports` because the API route ORs the
   * same two, and it has to: measured on NPM production, the Supervisor role (62
   * active users) and Supervisor Map (1, the ops manager) hold `viewMiscTasks`
   * but NOT `viewReports`, while HR holds `viewReports` but not `viewMiscTasks`.
   * Gating on `viewReports` alone would hide this card from every supervisor the
   * report was built for.
   *
   * NOTE: the hub PAGE is still gated on `viewReports` in App.tsx and
   * navConfig.tsx, so a supervisor cannot navigate here yet. Widening that is a
   * deliberate, separate decision — it also reveals the other cards — and is
   * called out in the handoff rather than slipped in here.
   */
  const showMiscTrend = !!config.showMiscTaskTrendingReport
    && (can(userRole, 'viewReports') || can(userRole, 'viewMiscTasks'));

  const week = useMemo(
    () => payPeriods.find((p) => p.PayPeriodID === payPeriodID),
    [payPeriods, payPeriodID],
  );
  const hasWeek = !!payPeriodID && !!week;

  /* ---- employees ---- */
  useEffect(() => {
    let cancelled = false;
    setEmpLoading(true);
    EmployeeViewManager.getEmployees()
      .then((rows) => { if (!cancelled) setEmployees(rows ?? []); })
      .catch((err) => {
        console.error('Failed to load employees', err);
        if (!cancelled) setEmployees([]);
      })
      .finally(() => { if (!cancelled) setEmpLoading(false); });
    return () => { cancelled = true; };
  }, []);

  /** Reset the hub cards when the shell's week changes — otherwise a stale
   *  "Downloaded 3:42 PM" reads as if the NEW week had been downloaded. The
   *  trending sub-view's busy state resets for the same reason: its window is
   *  anchored on the shell's week, so a run in flight is about the old one. */
  useEffect(() => {
    setCards({ attendance: 'idle', deadline: 'idle', ops: 'idle' });
    setCardDoneAt({});
    setTrendBusy('idle');
  }, [payPeriodID]);

  /**
   * Supervisors for the trending picker. Fetched on entry to that view rather
   * than on mount — it is one screen of three and most visits never open it.
   *
   * TWO RULES HERE, both learned the hard way; do not "optimise" either away.
   *
   * 1. `view` IS THE ONLY DEPENDENCY. An earlier version also listed
   *    `trendLoading` and `trendSupervisors.length` and guarded on them, which
   *    made the effect cancel itself: setting the loading flag changed a
   *    dependency, React ran the cleanup (marking this run cancelled), the
   *    re-run hit the `trendLoading` guard and returned without fetching, and
   *    the in-flight promise then found itself cancelled. The spinner ran
   *    forever, every time. State that the effect sets must never also be state
   *    the effect depends on.
   *
   * 2. `.finally` CLEARS THE FLAG UNCONDITIONALLY — it must not be inside an
   *    `if (!cancelled)`. `cancelled` exists only to suppress a superseded
   *    response from overwriting fresher state; a superseded run still has to
   *    put the spinner away, or it strands the UI. This also matters under
   *    React 18 StrictMode (this app mounts in it), which deliberately runs
   *    every effect twice with a cleanup in between.
   *
   * Re-entering the view refetches, which is cheap and keeps the list honest.
   * An already-valid selection survives it.
   */
  useEffect(() => {
    if (view !== 'miscTrend') return;
    let cancelled = false;
    setTrendLoading(true);
    getMiscTaskTrendSupervisors()
      .then((response) => {
        if (cancelled || !response) return;
        setTrendSupervisors(response.options);
        setTrendSupervisorID((current) =>
          current != null && response.options.some((o) => o.employeeID === current)
            ? current
            : resolveDefaultSupervisor(response.defaultEmployeeID, response.options));
      })
      .finally(() => setTrendLoading(false));
    return () => { cancelled = true; };
  }, [view]);

  const employeeByID = useMemo(() => {
    const m = new Map<number, EmployeeInfo>();
    employees.forEach((e) => m.set(e.employeeID, e));
    return m;
  }, [employees]);

  /** The payload carries supervisorID and locationID but no names (C4) —
   *  resolve both client-side, as EmployeeView.tsx:313-328 already does. */
  const supervisorName = useCallback((id: number): string => {
    if (!id) return '—';
    const s = employeeByID.get(id);
    return s ? displayName(s) : '—';
  }, [employeeByID]);

  const locationName = useCallback((id: number): string => {
    const l = locations?.find((x) => x.locationID === id);
    return l ? l.name : '—';
  }, [locations]);

  const locationLabel = useCallback((id: number): string => {
    const l = locations?.find((x) => x.locationID === id);
    return l ? `${l.altLocationID} - ${l.name}` : '—';
  }, [locations]);

  const locationOptions = useMemo(
    () => (locations ?? []).filter((l) => !l.testLocation),
    [locations],
  );

  /** Supervisors present among employees in the chosen location. */
  const supervisorOptions = useMemo(() => {
    const ids = new Set<number>();
    employees.forEach((e) => {
      if (locationID !== ALL && String(e.locationID) !== locationID) return;
      if (e.supervisorID) ids.add(e.supervisorID);
    });
    return [...ids]
      .map((id) => ({ id, label: supervisorName(id) }))
      .filter((s) => s.label !== '—')
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [employees, locationID, supervisorName]);

  const visibleEmployees = useMemo(() => {
    const q = query.trim();
    const digits = /^\d+$/.test(q);
    return employees
      .filter((e) => {
        if (activeOnly && !e.active) return false;
        if (locationID !== ALL && String(e.locationID) !== locationID) return false;
        if (supervisorID !== ALL && supervisorID !== '0' && String(e.supervisorID) !== supervisorID) return false;
        if (supervisorID === '0' && e.supervisorID) return false;
        if (!q) return true;
        return digits
          ? String(e.altEmployeeID ?? '').includes(q)
          : displayName(e).toLowerCase().includes(q.toLowerCase());
      })
      .sort((a, b) => displayName(a).localeCompare(displayName(b)));
  }, [employees, activeOnly, locationID, supervisorID, query]);

  const totalInScope = useMemo(
    () => employees.filter((e) => !activeOnly || e.active).length,
    [employees, activeOnly],
  );

  /** Location change resets the supervisor filter — today's behaviour. */
  const onLocationChange = (value: string) => {
    setLocationID(value);
    setSupervisorID(ALL);
  };

  /** Deselect an employee who has fallen out of the filter. */
  useEffect(() => {
    if (selectedEmployeeID == null) return;
    if (!visibleEmployees.some((e) => e.employeeID === selectedEmployeeID)) {
      setSelectedEmployeeID(null);
      setSelected(new Set());
      setGen('idle');
    }
  }, [visibleEmployees, selectedEmployeeID]);

  const selectedEmployee = selectedEmployeeID != null ? employeeByID.get(selectedEmployeeID) ?? null : null;

  /* ---- periods ---- */
  useEffect(() => {
    if (selectedEmployeeID == null) { setPeriods([]); return; }
    let cancelled = false;
    setPeriodsLoading(true);
    setPeriods([]);
    getEmployeeTimesheets(selectedEmployeeID)
      .then((res) => {
        if (cancelled) return;
        const sorted = [...(res.timesheets ?? [])].sort(
          (a, b) => b.payPeriod.payPeriodID - a.payPeriod.payPeriodID,
        );
        setPeriods(sorted);
        const groups = groupByYear(sorted);
        setOpenYears(new Set(groups.length ? [groups[0].key] : []));
      })
      .catch((err) => {
        console.error('Failed to load timesheets', err);
        if (!cancelled) setPeriods([]);
      })
      .finally(() => { if (!cancelled) setPeriodsLoading(false); });
    return () => { cancelled = true; };
  }, [selectedEmployeeID]);

  const groups = useMemo(() => groupByYear(periods), [periods]);

  const pickEmployee = (id: number) => {
    const next = selectedEmployeeID === id ? null : id;
    setSelectedEmployeeID(next);
    setSelected(new Set());
    setOpenYears(new Set());
    setGen('idle');
  };

  const toggleWeek = (timesheetID: number) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(timesheetID)) n.delete(timesheetID); else n.add(timesheetID);
      return n;
    });
    setGen('idle');
  };

  const quickPick = (kind: 'last4' | 'all' | 'none') => {
    if (kind === 'none') setSelected(new Set());
    if (kind === 'last4') setSelected(new Set(periods.slice(0, 4).map((p) => p.timesheetID)));
    if (kind === 'all') {
      setSelected(new Set(periods.map((p) => p.timesheetID)));
      setOpenYears(new Set(groups.map((g) => g.key)));
    }
    setGen('idle');
  };

  const toggleYear = (key: string) => {
    setOpenYears((prev) => {
      const n = new Set(prev);
      if (n.has(key)) n.delete(key); else n.add(key);
      return n;
    });
  };

  /* ---- hub card download ---- */
  const runCard = async (card: CardDef) => {
    if (!hasWeek || !week) return;
    setCards((c) => ({ ...c, [card.id]: 'busy' }));
    try {
      await card.download(payPeriodID, card.fileName(week.WeekNumber, week.AccountingYear));
      setCards((c) => ({ ...c, [card.id]: 'done' }));
      setCardDoneAt((d) => ({
        ...d,
        [card.id]: new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
      }));
      showToast(`${card.name} downloaded — week ${week.WeekNumber}`);
    } catch (err) {
      console.error(`Failed to download ${card.name}`, err);
      setCards((c) => ({ ...c, [card.id]: 'idle' }));
      createPopup('Error', `Failed to download the ${card.name}. Please try again.`);
    }
  };

  /**
   * Builds one Misc Task Trending file.
   *
   * The api module raises its own alert on failure (it is the only layer that can
   * decode the arraybuffer error body into a readable message), so this only has
   * to manage the busy state and the success toast.
   */
  /**
   * Options for the supervisor picker.
   *
   * `count` carries the direct-report number, which FloatMenuButton renders on
   * the right of each row — and it earns its place: NPM's employee table has
   * genuinely duplicated names, so the count plus the employee number is what
   * separates two rows that otherwise read identically.
   */
  const trendMenuOptions: FloatMenuOption[] = useMemo(
    () => trendSupervisors.map((s) => ({
      value: String(s.employeeID),
      label: supervisorLabel(s),
      count: s.directReports,
    })),
    [trendSupervisors],
  );

  /** The trigger shows the same text as the menu row, minus the direct-report
   *  count — the trigger has no room for it, and the menu renders it separately
   *  on the right of each option. */
  const trendSupervisorLabel = useMemo(() => {
    if (trendSupervisorID == null) return 'Choose a supervisor';
    const match = trendMenuOptions.find((o) => o.value === String(trendSupervisorID));
    return match ? match.label : 'Choose a supervisor';
  }, [trendSupervisorID, trendMenuOptions]);

  const runTrend = async (format: MiscTaskTrendFormat) => {
    if (!hasWeek || !week || trendSupervisorID == null) return;
    setTrendBusy(format);
    try {
      const who = trendSupervisors.find((s) => s.employeeID === trendSupervisorID)?.name ?? '';
      const ok = await downloadMiscTaskTrendReport(
        payPeriodID,
        trendSupervisorID,
        format,
        miscTaskTrendFileName(who, week.WeekNumber, week.AccountingYear, format),
      );
      if (ok) showToast(`Misc Task Trending downloaded — ${who}`);
    } finally {
      setTrendBusy('idle');
    }
  };

  /* ---- generate ---- */
  const generate = async () => {
    if (!selectedEmployee || selected.size === 0) return;
    const ids = [...selected];
    const def = REPORTS.find((r) => r.id === report)!;
    setGen('busy');
    try {
      const altID = String(selectedEmployee.altEmployeeID ?? selectedEmployee.employeeID);
      // Only ever send false. The server ANDs this with the role check, so a
      // true here could never grant comments — but sending it only when it
      // means something keeps the request honest about intent.
      const comments = canSeeComments ? includeComments : undefined;
      const fn = report === 'audit' ? generateAuditReport
        : report === 'punch' ? generateTimePunchReport
          : generateTimesheetReport;
      const ok = await fn(selectedEmployee.employeeID, ids, altID, comments);
      if (ok) {
        setGen('idle');
        showToast(`${def.name} downloaded — ${ids.length} week${ids.length === 1 ? '' : 's'}, ${displayName(selectedEmployee)}`);
      } else {
        setGen('failed');
      }
    } catch (err) {
      console.error('Failed to generate report', err);
      setGen('failed');
    }
  };

  /* ── render: hub ───────────────────────────────────────────────────────── */

  const renderHub = () => (
    <>
      <div className="page-head">
        <div className="ph-ic"><FileBarChart2 /></div>
        <div>
          <h1>
            Reports
            <span className="periodchip">
              {hasWeek && week
                ? `Week ${week.WeekNumber} · ${formatRange(String(week.StartDate), String(week.EndDate))}`
                : 'No week selected'}
            </span>
          </h1>
          <div className="ph-sub">
            Weekly spreadsheets for the whole company, and per-employee audit and timesheet packets.
            Everything here is read-only — nothing on this page changes a timesheet.
          </div>
        </div>
      </div>

      <div className="sect">
        <h2>By week</h2>
        <span className="sub">One click. Downloads a spreadsheet for the week in the top bar.</span>
      </div>
      <div className="rph-cards">
        {CARDS.map((c) => {
          const state = cards[c.id];
          const { Icon } = c;
          return (
            <div key={c.id} className={`rph-card${state === 'busy' ? ' busy' : ''}`} style={{ ['--tone' as string]: c.tone }}>
              <div className="rc-top">
                <div className="rc-ic"><Icon /></div>
                <div>
                  <div className="rc-name">{c.name}</div>
                  <div className="rc-tags">
                    <span className="tag out">.xlsx</span>
                    {hasWeek && week && <span className="tag"><Calendar />Week {week.WeekNumber}</span>}
                  </div>
                </div>
              </div>
              <div className="rc-desc">{c.desc}</div>
              <div className="rc-foot">
                {state === 'busy' ? (
                  <button className="dlbtn working" disabled><span className="spin" />Building…</button>
                ) : (
                  <button className="dlbtn" onClick={() => runCard(c)} disabled={!hasWeek}>
                    <Download />{state === 'done' ? 'Download again' : 'Download'}
                  </button>
                )}
                {!hasWeek && <span className="rc-hint">Pick a week in the top bar first.</span>}
                {hasWeek && state === 'done' && (
                  <span className="rc-hint done"><Check />Downloaded {cardDoneAt[c.id]}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="sect">
        <h2>By employee</h2>
        <span className="sub">Pick a person and the weeks you need, then build a PDF.</span>
      </div>
      <button className="rph-bigcard" onClick={() => { setView('audit'); window.scrollTo(0, 0); }}>
        <div className="bc-ic"><FileSearch /></div>
        <div className="bc-main">
          <div className="bc-name">
            Audit &amp; Timesheet Reports
            <span className="tag scope-emp"><User />One employee</span>
            <span className="tag out">PDF</span>
          </div>
          <div className="bc-desc">
            Build a packet for one employee across any weeks they have on file — the timecards on
            their own, or with the full record of every change made to them.
          </div>
          <div className="bc-list">
            {REPORTS.map((r) => (
              <span className="bc-item" key={r.id}>
                <FileText />{r.name}{r.isNew && <span className="new">NEW</span>}
              </span>
            ))}
          </div>
        </div>
        <span className="bc-go"><ChevronRight /></span>
      </button>

      {showMiscTrend && (
        <>
          <div className="sect">
            <h2>By supervisor</h2>
            <span className="sub">Eight weeks of misc task spend, rolled up by supervisor.</span>
          </div>
          <button
            className="rph-bigcard"
            onClick={() => { setView('miscTrend'); window.scrollTo(0, 0); }}
          >
            <div className="bc-ic"><TrendingUp /></div>
            <div className="bc-main">
              <div className="bc-name">
                Misc Task Trending
                <span className="tag new-tag">NEW</span>
                <span className="tag scope-emp"><Users />One organization</span>
                <span className="tag out">PDF</span>
                <span className="tag out">.xlsx</span>
              </div>
              <div className="bc-desc">
                Eight weeks of approved misc task claims for a supervisor and everyone beneath
                them — summed by supervisor lowest to highest, then broken down employee by
                employee.
              </div>
              <div className="bc-list">
                <span className="bc-item"><TrendingUp />Weekly trend chart</span>
                <span className="bc-item"><Users />Supervisor rollup</span>
                <span className="bc-item"><FileSpreadsheet />Claim-level audit trail</span>
              </div>
            </div>
            <span className="bc-go"><ChevronRight /></span>
          </button>
        </>
      )}
    </>
  );

  /* ── render: misc task trending ────────────────────────────────────────── */

  const renderMiscTrend = () => {
    const ready = hasWeek && trendSupervisorID != null;
    const busy = trendBusy !== 'idle';

    return (
      <>
        <div className="crumbs">
          <button onClick={() => { setView('hub'); window.scrollTo(0, 0); }}>Reports</button>
          <ChevronRight />
          <span className="here">Misc Task Trending</span>
        </div>
        <div className="page-head">
          <div className="ph-ic"><TrendingUp /></div>
          <div>
            <h1>
              Misc Task Trending
              <span className="periodchip">
                {hasWeek && week
                  ? `8 weeks ending Week ${week.WeekNumber} · ${formatRange(String(week.StartDate), String(week.EndDate))}`
                  : 'No week selected'}
              </span>
            </h1>
            <div className="ph-sub">
              Approved misc task claims for a supervisor and their whole organization, summed by
              supervisor and broken down by employee. The window ends at the week in the top bar.
            </div>
          </div>
        </div>

        <div className="rph-block">
          <div className="rph-bh">
            <UserSearch />
            <h3>Supervisor</h3>
          </div>
          <div className="mtt-body">
            {trendLoading ? (
              <div className="mtt-state"><RefreshCw />Loading supervisors…</div>
            ) : trendSupervisors.length === 0 ? (
              <div className="mtt-state">
                <Info />
                No supervisors are available to you. Only people with at least one active direct
                report can be selected.
              </div>
            ) : (
              // The shared v2 filter selector, type-to-filter enabled — the same
              // control the Project Map uses for its supervisor filter. NPM has
              // 60+ supervisors, which is well past where a plain <select> stops
              // being usable. Its menu portals to <body>, so it is not clipped by
              // the block's `overflow: hidden`.
              <FloatMenuButton
                // "Super", not "Supervisor or ops manager": the trigger splits a
                // fixed width between label and value, and the long form squeezed
                // the name down to "BARRERA, WALTER (36…". The block header above
                // already says SUPERVISOR, and this matches the Project Map's
                // filter bar, which is the control users will recognise it from.
                label="Super"
                valueLabel={trendSupervisorLabel}
                isPlaceholder={trendSupervisorID == null}
                isActive={trendSupervisorID != null}
                options={trendMenuOptions}
                selectedValue={trendSupervisorID == null ? '' : String(trendSupervisorID)}
                onSelect={(v) => setTrendSupervisorID(v === '' ? null : Number(v))}
                disabled={busy}
                searchable
              />
            )}
          </div>
          {trendSupervisors.length > 0 && !trendLoading && (
            <div className="rph-blocknote">
              <Users />
              {/* One expression, not adjacent JSX lines — a line break after a
                  closing tag becomes a rendered space, which put a gap before
                  the comma ("beneath them , however"). */}
              <span>
                The report covers this person and <b>everyone beneath them</b>, however many
                levels deep. Supervisors are summed lowest to highest, then each one&apos;s
                employees.
              </span>
            </div>
          )}
        </div>

        <div className="rph-block">
          <div className="rph-bh">
            <ListChecks />
            <h3>What&apos;s included</h3>
          </div>
          <ul className="mtt-notes">
            {MISC_TASK_TREND_INCLUSIONS.map((line) => (
              <li key={line}><Check />{line}</li>
            ))}
          </ul>
        </div>

        <div className="rph-act">
          <div className="summ">
            {!hasWeek && <><AlertTriangle className="warn-ic" /> Pick a week in the top bar first.</>}
            {hasWeek && busy && <>Building the {trendBusy === 'pdf' ? 'PDF' : 'workbook'}…</>}
            {hasWeek && !busy && trendSupervisorID != null && (
              <>8 weeks ending <b>Week {week?.WeekNumber}</b></>
            )}
          </div>
          <div className="spacer" />
          <button className="gen ghost" disabled={!ready || busy} onClick={() => runTrend('xlsx')}>
            {trendBusy === 'xlsx' ? <span className="spin" /> : <FileSpreadsheet />}
            Excel
          </button>
          <button className="gen" disabled={!ready || busy} onClick={() => runTrend('pdf')}>
            {trendBusy === 'pdf' ? <span className="spin" /> : <FileText />}
            Download PDF
          </button>
        </div>
      </>
    );
  };

  /* ── render: sub-view ──────────────────────────────────────────────────── */

  const highlight = (text: string, q: string) => {
    if (!q) return text;
    const i = text.toLowerCase().indexOf(q.toLowerCase());
    if (i < 0) return text;
    return (
      <>
        {text.slice(0, i)}<mark>{text.slice(i, i + q.length)}</mark>{text.slice(i + q.length)}
      </>
    );
  };

  const renderEmployeePanel = () => {
    const q = query.trim();
    const digits = /^\d+$/.test(q);
    return (
      <div className="rph-panel">
        <div className="rph-filters">
          <div className="rph-search">
            <Search />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name or employee ID"
              aria-label="Search employees by name or employee ID"
              autoComplete="off"
            />
          </div>
          <div className="rph-selrow">
            <div className="rph-fld">
              <label htmlFor="rph-loc">Location</label>
              <select id="rph-loc" value={locationID} onChange={(e) => onLocationChange(e.target.value)}>
                <option value={ALL}>ALL</option>
                {locationOptions.map((l) => (
                  <option key={l.locationID} value={String(l.locationID)}>{l.altLocationID} - {l.name}</option>
                ))}
              </select>
            </div>
            <div className="rph-fld">
              <label htmlFor="rph-sup">Supervisor</label>
              <select id="rph-sup" value={supervisorID} onChange={(e) => setSupervisorID(e.target.value)}>
                <option value={ALL}>ALL</option>
                <option value="0">0 - Supervisors</option>
                {supervisorOptions.map((s) => (
                  <option key={s.id} value={String(s.id)}>{s.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="rph-actrow">
            <label className="rph-sw">
              <input
                className="rph-sr"
                type="checkbox"
                checked={activeOnly}
                onChange={(e) => setActiveOnly(e.target.checked)}
              />
              <span className="switch" aria-hidden="true" />
              Active only
            </label>
            <span className="rph-count" aria-live="polite">
              <b>{visibleEmployees.length}</b> of {totalInScope}
            </span>
          </div>
        </div>

        {empLoading ? (
          <div className="rph-noresult"><div className="t">Loading employees…</div></div>
        ) : visibleEmployees.length ? (
          <div className="rph-emplist">
            {visibleEmployees.map((e) => {
              const name = displayName(e);
              const h = hueOf(name);
              return (
                <button
                  key={e.employeeID}
                  className={`rph-erow${selectedEmployeeID === e.employeeID ? ' on' : ''}`}
                  onClick={() => pickEmployee(e.employeeID)}
                  aria-pressed={selectedEmployeeID === e.employeeID}
                >
                  <span
                    className="rph-av"
                    style={{ background: `hsl(${h} 62% 92%)`, color: `hsl(${h} 58% 34%)` }}
                  >
                    {initialsOf(e.firstName, e.lastName)}
                  </span>
                  <span className="en">
                    <span className="nm">{digits ? name : highlight(name, q)}</span>
                    <span className="sub">{locationName(e.locationID)} · {supervisorName(e.supervisorID)}</span>
                  </span>
                  {!e.active && <span className="inact">Inactive</span>}
                  <span className="eid">#{digits ? highlight(String(e.altEmployeeID ?? ''), q) : String(e.altEmployeeID ?? '')}</span>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="rph-noresult">
            <UserSearch />
            <div className="t">No employee matches</div>
            <div className="s">
              {q ? `Nothing for “${q}” in this location and crew.` : 'No one in this location and crew.'}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderPeriodBlock = () => {
    if (periodsLoading) {
      return (
        <div className="rph-block">
          <div className="rph-bh"><Calendar /><h3>Weeks</h3></div>
          <div className="rph-noper"><div className="t">Loading weeks…</div></div>
        </div>
      );
    }
    if (!periods.length) {
      return (
        <div className="rph-block">
          <div className="rph-bh"><Calendar /><h3>Weeks</h3></div>
          <div className="rph-noper">
            <div className="npi"><CalendarOff /></div>
            <div className="t">No weeks on file for {selectedEmployee?.firstName}</div>
            <div className="s">
              Either they have never submitted a timesheet, or every week they had was unlocked after
              submission — unlocking removes the week from this list even though the punches are
              still in the system.
            </div>
          </div>
        </div>
      );
    }

    const n = selected.size;
    return (
      <div className="rph-block">
        <div className="rph-bh">
          <Calendar />
          <h3>Weeks</h3>
          <div className="bh-right">
            <button className="rph-qp" onClick={() => quickPick('last4')}>Last 4</button>
            <button className="rph-qp" onClick={() => quickPick('all')}>Select all ({periods.length})</button>
            <button className="rph-qp clear" onClick={() => quickPick('none')} disabled={!n}>Clear</button>
          </div>
        </div>

        <div className="rph-selline" aria-live="polite">
          <ListChecks />
          {n
            ? <span><b>{n}</b> week{n === 1 ? '' : 's'} selected</span>
            : <span className="none">No weeks selected yet — pick at least one.</span>}
        </div>

        <div className="rph-years">
          {groups.map((g) => {
            const open = openYears.has(g.key);
            const picked = g.periods.filter((p) => selected.has(p.timesheetID)).length;
            return (
              <div key={g.key} className={`rph-yr${open ? ' open' : ''}`}>
                <button className="rph-yrh" onClick={() => toggleYear(g.key)} aria-expanded={open}>
                  <span className="chev"><ChevronRight /></span>
                  <span className="y">{g.label}</span>
                  <span className="yl">accounting year</span>
                  <span className="n">
                    {picked
                      ? <span className="cc">{picked} picked</span>
                      : `${g.periods.length} week${g.periods.length === 1 ? '' : 's'}`}
                  </span>
                </button>
                {open && (
                  <div>
                    {g.periods.map((p, i) => {
                      const missing = missingWeeksBetween(g.periods[i - 1], p, g.key);
                      const on = selected.has(p.timesheetID);
                      const isCurrent = p.payPeriod.payPeriodID === payPeriodID;
                      return (
                        <React.Fragment key={p.timesheetID}>
                          {missing.length > 0 && (
                            <div className="rph-gap">
                              <CalendarOff />
                              <span className="wn">Week {missing.join(', ')}</span> — no timesheet on file
                            </div>
                          )}
                          <label className={`rph-wk${on ? ' on' : ''}`}>
                            <input
                              className="rph-sr"
                              type="checkbox"
                              checked={on}
                              onChange={() => toggleWeek(p.timesheetID)}
                            />
                            <span className="rph-cb" aria-hidden="true"><Check /></span>
                            <span className="wn">Week {p.payPeriod.weekNumber}</span>
                            <span className="wd">{formatRange(p.payPeriod.startDate, p.payPeriod.endDate)}</span>
                            {isCurrent && <span className="cur">Current</span>}
                          </label>
                        </React.Fragment>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="rph-blocknote">
          <Info />
          <span>
            <b>This list is not a complete work history.</b> It shows the weeks that have a timesheet
            record. A week that was never submitted, or was unlocked after submission, has no record
            and cannot appear here — even though its punches survive.
          </span>
        </div>
      </div>
    );
  };

  const renderChooser = () => (
    <div className="rph-block">
      <fieldset>
        <div className="rph-bh"><FileText /><legend>Report</legend></div>
        <div className="rph-opts">
          {REPORTS.map((r) => (
            <label key={r.id} className={`rph-opt${report === r.id ? ' on' : ''}`}>
              <input
                className="rph-sr"
                type="radio"
                name="rph-report"
                value={r.id}
                checked={report === r.id}
                onChange={() => { setReport(r.id); setGen('idle'); }}
              />
              <span className="rd" aria-hidden="true" />
              <span className="ro-main">
                <span className="ro-name">
                  {r.name}
                  {r.isNew && <span className="new">NEW</span>}
                  <span className="tag out">PDF</span>
                </span>
                <span className="ro-desc">{r.desc}</span>
                {r.legend && (
                  <span className="rph-leg">
                    <span className="ll">Prints with</span>
                    {r.legend.map(([hex, label]) => (
                      <span className="lg" key={label}>
                        <span className="sw" style={{ background: hex }} />{label}
                      </span>
                    ))}
                  </span>
                )}
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      {canSeeComments && (
        <label className="rph-copt">
          <input
            className="rph-sr"
            type="checkbox"
            checked={includeComments}
            onChange={(e) => { setIncludeComments(e.target.checked); setGen('idle'); }}
          />
          <span className="switch" aria-hidden="true" />
          <span className="ct">
            <span className="cl">Include employee comments <MessageSquareText /></span>
            <span className="ch">
              The note the worker typed when submitting appears as a Comments row on each timecard.
              Turn this off for packets leaving the office — to the employee, a union rep, or outside
              counsel.
            </span>
            <span className="call3">
              Applies to all three reports. Only Admin, HR and Payroll ever see comments; this can
              only take them away.
            </span>
          </span>
        </label>
      )}
    </div>
  );

  const renderActionArea = () => {
    const n = selected.size;
    const def = REPORTS.find((r) => r.id === report)!;

    if (gen === 'busy') {
      return (
        <div className="rph-progwrap">
          <div className="pr-top">
            <span className="spin" />
            {/* One honest step. The request is a single synchronous call that
                returns a finished PDF — there is no per-week progress to report,
                so we don't invent a counter. */}
            <span className="step">
              Rendering {n} timecard{n === 1 ? '' : 's'} and merging them into one PDF…
            </span>
          </div>
          <div className="rph-prog"><span className="bar" /></div>
          <div className="pr-note">
            Generation runs in one request — leaving the page cancels it. The file downloads when the
            whole packet is ready.
          </div>
        </div>
      );
    }

    return (
      <>
        {n > WARN_AT && (
          <div className="rph-banner warn">
            <AlertTriangle />
            <span className="bt">
              <b>That&apos;s {n} weeks.</b> This will take a few minutes to build and produce a large
              PDF. Fine if you need the full history — otherwise narrow it down.
            </span>
          </div>
        )}
        {gen === 'failed' && (
          <div className="rph-banner fail">
            <AlertOctagon />
            <span className="bt">
              <b>The report didn&apos;t finish.</b> Nothing was downloaded — there&apos;s no partial
              file. Try again, or with fewer weeks if it keeps timing out.
            </span>
            <button className="bact" onClick={generate}><RefreshCw />Try again</button>
          </div>
        )}
        <div className="rph-act">
          <span className="summ">
            {n ? <><b>{n}</b> week{n === 1 ? '' : 's'}</> : 'No weeks selected'}
            <span className="sep">·</span>{def.name}
            {canSeeComments && (
              <>
                <span className="sep">·</span>
                <span className="m">comments {includeComments ? 'on' : 'off'}</span>
              </>
            )}
          </span>
          <span className="spacer" />
          <button className="gen" onClick={generate} disabled={!n}>
            <Download />Generate {def.name.replace(' Report', '')} PDF
          </button>
        </div>
      </>
    );
  };

  const renderDetail = () => {
    if (!selectedEmployee) {
      return (
        <div className="rph-blank">
          <div className="bl-ic"><FileSearch /></div>
          <h2>Build a report for one employee</h2>
          <div className="bl-sub">
            Three PDFs come out of this page — the timecards on their own, or the timecards with the
            full record of what was changed and who changed it.
          </div>
          <div className="bl-steps">
            <div className="bl-step">
              <span className="n">1</span>
              <span className="tx"><b>Pick an employee</b> from the list. Type a name, or type digits to match an employee ID.</span>
            </div>
            <div className="bl-step">
              <span className="n">2</span>
              <span className="tx"><b>Pick the weeks.</b> Nothing is selected up front — one week, a month, or the whole history.</span>
            </div>
            <div className="bl-step">
              <span className="n">3</span>
              <span className="tx"><b>Pick the report</b> and generate. One employee per packet.</span>
            </div>
          </div>
          <div className="bl-foot">
            <Info />
            <span>These reports read history only. Generating one changes nothing and is not recorded against the employee.</span>
          </div>
        </div>
      );
    }

    const name = displayName(selectedEmployee);
    const h = hueOf(name);
    return (
      <>
        <div className="rph-head">
          <span
            className="av"
            style={{ background: `linear-gradient(140deg, hsl(${h} 55% 58%), hsl(${h} 62% 44%))` }}
          >
            {initialsOf(selectedEmployee.firstName, selectedEmployee.lastName)}
          </span>
          <div>
            <h2>{name}<span className="id">#{String(selectedEmployee.altEmployeeID ?? '')}</span></h2>
            <div className="hsub">
              {locationLabel(selectedEmployee.locationID)}
              <span className="dot" />
              Supervisor <b>{supervisorName(selectedEmployee.supervisorID)}</b>
              <span className="dot" />
              {selectedEmployee.active ? 'Active' : 'Inactive'}
              <span className="dot" />
              <span className="mono">
                {periods.length} week{periods.length === 1 ? '' : 's'} on file
              </span>
            </div>
          </div>
          <div className="hright">
            <button
              className="clearbtn"
              onClick={() => { setSelectedEmployeeID(null); setSelected(new Set()); setGen('idle'); }}
            >
              <X />Clear
            </button>
          </div>
        </div>
        {renderPeriodBlock()}
        {periods.length > 0 && !periodsLoading && (
          <>
            {renderChooser()}
            {renderActionArea()}
          </>
        )}
      </>
    );
  };

  const renderSub = () => (
    <>
      <div className="crumbs">
        <button onClick={() => { setView('hub'); window.scrollTo(0, 0); }}>Reports</button>
        <ChevronRight />
        <span className="here">Audit &amp; Timesheet Reports</span>
      </div>
      <div className="page-head">
        <div className="ph-ic"><FileSearch /></div>
        <div>
          <h1>Audit &amp; Timesheet Reports</h1>
          <div className="ph-sub">
            One employee at a time. Pick the weeks you need and which of the three PDFs to build.
          </div>
        </div>
      </div>
      <div className="rph-split">
        {renderEmployeePanel()}
        <div>{renderDetail()}</div>
      </div>
    </>
  );

  return (
    <div className="rph">
      {view === 'hub' && renderHub()}
      {view === 'audit' && renderSub()}
      {view === 'miscTrend' && renderMiscTrend()}
      <div className="rph-toasts">
        {toasts.map((t) => (
          <div className="rph-toast" key={t.id}><CheckCircle2 />{t.msg}</div>
        ))}
      </div>
    </div>
  );
};

export default ReportsHubPage;
